import { RefObject, useEffect, useState } from 'react'

/**
 * Hook that tracks the width of an element using ResizeObserver.
 *
 * @param elementRef - A ref to the element to observe
 * @param fallbackWidth - Optional fallback width for SSR or when element is not available (defaults to window.innerWidth)
 * @returns The current width of the element
 */
export const useElementWidth = (elementRef: RefObject<HTMLElement | null>, fallbackWidth?: number): number => {
  const getInitialWidth = () => {
    if (typeof window === 'undefined') return fallbackWidth ?? 0
    return fallbackWidth ?? window.innerWidth
  }

  const [width, setWidth] = useState(getInitialWidth)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    setWidth(element.offsetWidth)

    if (typeof ResizeObserver === 'undefined') {
      const handleResize = () => {
        if (element) {
          setWidth(element.offsetWidth)
        }
      }
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.borderBoxSize && entry.borderBoxSize.length > 0) {
          setWidth(entry.borderBoxSize[0].inlineSize)
        } else {
          setWidth(entry.contentRect.width)
        }
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [elementRef])

  return width
}
